import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loader2, RefreshCw, Filter, Mail, FileText, Pause, Play, X, ExternalLink, TrendingUp, Users, Clock, Target, Calculator } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

function authFetch(url, opts = {}) {
  const token = localStorage.getItem('auth_token') || '';
  return fetch(`${API}${url}`, {
    ...opts,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
}

function StatBox({ label, value, sub, color = 'emerald' }) {
  const colorClass = {
    emerald: 'text-emerald-400',
    amber: 'text-amber-400',
    cyan: 'text-cyan-400',
    red: 'text-red-400',
    slate: 'text-slate-300',
  }[color];
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
      <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono mb-1">{label}</p>
      <p className={`text-2xl font-bold ${colorClass}`}>{value ?? '—'}</p>
      {sub && <p className="text-[11px] text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

function ExperiencesPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('last_30d'); // last_7d | last_30d | all_time

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch('/api/admin/dashboard/experiences-stats');
      if (r.ok) setData(await r.json());
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center gap-2 text-slate-400 py-12 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>;
  if (!data) return <div className="text-slate-400 py-8 text-center">Sin datos</div>;

  const exps = [
    { key: 'nelson', label: 'Experiencia Nelson', color: 'red', path: '/experiencia' },
    { key: 'yo_futuro', label: 'Yo del Futuro', color: 'cyan', path: '/yo-del-futuro' },
    { key: 'espejo', label: 'Espejo', color: 'emerald', path: '/espejo' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-slate-400 mr-2">Periodo:</span>
        {[
          { k: 'last_7d', l: '7 días' },
          { k: 'last_30d', l: '30 días' },
          { k: 'all_time', l: 'Total' },
        ].map(p => (
          <button
            key={p.k}
            onClick={() => setPeriod(p.k)}
            className={`px-3 py-1 text-xs rounded-md border ${period === p.k ? 'bg-emerald-500 text-black border-emerald-500 font-bold' : 'bg-slate-900 text-slate-300 border-slate-700 hover:border-slate-500'}`}
            data-testid={`exp-period-${p.k}`}
          >
            {p.l}
          </button>
        ))}
        <Button onClick={load} size="sm" variant="ghost" className="ml-auto text-slate-400 hover:text-white"><RefreshCw className="w-3.5 h-3.5 mr-1" />Refrescar</Button>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {exps.map(exp => {
          const stats = data[exp.key]?.[period] || {};
          return (
            <Card key={exp.key} className="bg-slate-950 border-slate-800">
              <CardHeader className="pb-3">
                <CardTitle className={`text-${exp.color}-400 text-base flex items-center justify-between`}>
                  {exp.label}
                  <a href={exp.path} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-white">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <StatBox label="Iniciados" value={stats.started || 0} color="slate" />
                  <StatBox label="Completados" value={stats.completed || 0} color={exp.color} />
                </div>
                {exp.key === 'yo_futuro' && (
                  <StatBox label="Form enviado" value={stats.form_submitted || 0} color="cyan" sub="Pasaron del intro al chat" />
                )}
                <div className="bg-slate-900/40 rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono mb-1">Tasa de finalización</p>
                  <p className={`text-3xl font-bold text-${exp.color}-400`}>{stats.completion_rate || 0}%</p>
                  <div className="mt-2 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div className={`h-full bg-${exp.color}-500 transition-all`} style={{ width: `${Math.min(stats.completion_rate || 0, 100)}%` }} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function Reto21Panel() {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ sustancia: '', drip_status: '', search: '' });
  const [selected, setSelected] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const r = await authFetch('/api/admin/dashboard/reto21-stats');
      if (r.ok) setStats(await r.json());
    } catch (e) { /* ignore */ }
  }, []);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (filters.sustancia) qs.set('sustancia', filters.sustancia);
    if (filters.drip_status) qs.set('drip_status', filters.drip_status);
    if (filters.search) qs.set('search', filters.search);
    qs.set('limit', '100');
    try {
      const r = await authFetch(`/api/admin/dashboard/reto21-leads?${qs}`);
      if (r.ok) {
        const j = await r.json();
        setLeads(j.leads || []);
      }
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [filters]);

  useEffect(() => { loadStats(); loadLeads(); }, [loadStats, loadLeads]);

  const togglePauseDrip = async (leadId, currentlyActive) => {
    const action = currentlyActive ? 'pause-drip' : 'resume-drip';
    await authFetch(`/api/admin/dashboard/reto21-lead/${leadId}/${action}`, { method: 'POST' });
    loadLeads();
    loadStats();
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const getDripBadge = (lead) => {
    const day = lead.drip_last_day_sent || 0;
    if (!lead.drip_active && day < 21) return <span className="text-[10px] px-2 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/30">Cancelado</span>;
    if (day >= 21) return <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">Completó 21d</span>;
    if (lead.drip_active) return <span className="text-[10px] px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">Día {day}/21</span>;
    return <span className="text-[10px] px-2 py-0.5 rounded bg-slate-700 text-slate-300">Sin drip</span>;
  };

  const isNew = (iso) => {
    if (!iso) return false;
    return Date.now() - new Date(iso).getTime() < 24 * 3600 * 1000;
  };

  return (
    <div className="space-y-5">
      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <StatBox label="Total leads" value={stats?.total_leads} color="slate" sub={<><Users className="w-3 h-3 inline mr-0.5" />all-time</>} />
        <StatBox label="Últimos 30d" value={stats?.last_30d} color="emerald" />
        <StatBox label="Últimos 7d" value={stats?.last_7d} color="emerald" />
        <StatBox label="Drip activo" value={stats?.drip_active} color="cyan" />
        <StatBox label="Completaron" value={stats?.drip_completed} color="amber" sub={`${stats?.completion_rate || 0}% del total`} />
        <StatBox label="Bajas" value={stats?.unsubscribed} color="red" />
        <StatBox label="Día prom." value={stats?.avg_drip_day} color="slate" sub={<><Clock className="w-3 h-3 inline mr-0.5" />avance medio</>} />
      </div>

      {/* Distribución por sustancia */}
      {stats?.by_substance?.length > 0 && (
        <Card className="bg-slate-950 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-300 flex items-center gap-2"><Target className="w-4 h-4" />Distribución por sustancia</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.by_substance.map(s => {
                const pct = stats.total_leads ? Math.round(100 * s.count / stats.total_leads) : 0;
                return (
                  <div key={s.sustancia} className="flex items-center gap-3 text-sm">
                    <span className="w-32 text-slate-300">{s.sustancia}</span>
                    <div className="flex-1 h-5 bg-slate-900 rounded overflow-hidden">
                      <div className="h-full bg-emerald-500/70 flex items-center px-2 text-[10px] text-white font-bold" style={{ width: `${Math.max(pct, 4)}%` }}>
                        {pct}%
                      </div>
                    </div>
                    <span className="text-slate-400 text-xs w-12 text-right">{s.count}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtros */}
      <Card className="bg-slate-950 border-slate-800">
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="w-4 h-4 text-slate-500" />
            <select
              value={filters.sustancia}
              onChange={e => setFilters({ ...filters, sustancia: e.target.value })}
              className="px-3 py-1.5 bg-slate-900 border border-slate-700 rounded text-sm text-slate-200"
              data-testid="reto21-filter-sustancia"
            >
              <option value="">Todas las sustancias</option>
              <option>Alcohol</option>
              <option>Cocaína</option>
              <option>Pasta Base</option>
              <option>Marihuana</option>
              <option>Benzodiacepinas / Opioides</option>
            </select>
            <select
              value={filters.drip_status}
              onChange={e => setFilters({ ...filters, drip_status: e.target.value })}
              className="px-3 py-1.5 bg-slate-900 border border-slate-700 rounded text-sm text-slate-200"
              data-testid="reto21-filter-status"
            >
              <option value="">Todo el estado</option>
              <option value="active">Drip activo</option>
              <option value="completed">Completaron 21d</option>
              <option value="unsubscribed">Cancelaron</option>
            </select>
            <Input
              placeholder="Buscar por nombre o email…"
              value={filters.search}
              onChange={e => setFilters({ ...filters, search: e.target.value })}
              className="max-w-xs bg-slate-900 border-slate-700 h-8 text-sm"
              data-testid="reto21-filter-search"
            />
            <Button onClick={() => { loadLeads(); loadStats(); }} size="sm" variant="ghost" className="ml-auto"><RefreshCw className="w-3.5 h-3.5 mr-1" />Actualizar</Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabla leads */}
      <Card className="bg-slate-950 border-slate-800">
        <CardHeader className="pb-2 flex-row justify-between items-center">
          <CardTitle className="text-sm text-slate-300 flex items-center gap-2"><Mail className="w-4 h-4" />Leads del Reto 21 ({leads.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400 gap-2"><Loader2 className="w-4 h-4 animate-spin" />Cargando…</div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12 text-slate-500 text-sm">No hay leads para los filtros seleccionados.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                    <th className="px-4 py-3 text-left">Lead</th>
                    <th className="px-4 py-3 text-left">Email</th>
                    <th className="px-4 py-3 text-left">Sustancia</th>
                    <th className="px-4 py-3 text-left">Drip</th>
                    <th className="px-4 py-3 text-left">Registro</th>
                    <th className="px-4 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map(lead => (
                    <tr key={lead.lead_id} className="border-b border-slate-800/60 hover:bg-slate-900/40">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="text-slate-200 font-medium flex items-center gap-2">
                              {lead.nombre}
                              {isNew(lead.created_at) && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500 text-black font-bold">NUEVO</span>}
                            </p>
                            <p className="text-[11px] text-slate-500">{lead.edad} años · {lead.anos_consumo} años de consumo</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-400 font-mono text-xs">{lead.email}</td>
                      <td className="px-4 py-3 text-slate-300">{lead.sustancia}</td>
                      <td className="px-4 py-3">{getDripBadge(lead)}</td>
                      <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{fmtDate(lead.created_at)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => setSelected(lead)}
                          className="text-emerald-400 hover:text-emerald-300 text-xs mr-3"
                          data-testid={`reto21-detail-${lead.lead_id}`}
                        >
                          Ver detalle
                        </button>
                        {lead.drip_last_day_sent < 21 && (
                          <button
                            onClick={() => togglePauseDrip(lead.lead_id, lead.drip_active)}
                            className={`text-xs ${lead.drip_active ? 'text-amber-400 hover:text-amber-300' : 'text-cyan-400 hover:text-cyan-300'}`}
                            data-testid={`reto21-toggle-drip-${lead.lead_id}`}
                          >
                            {lead.drip_active ? <><Pause className="w-3 h-3 inline" /> Pausar drip</> : <><Play className="w-3 h-3 inline" /> Reanudar drip</>}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal detalle */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setSelected(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-slate-900 border-b border-slate-800 px-6 py-4 flex items-center justify-between">
              <h3 className="text-white text-lg">Detalle del lead</h3>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-[10px] uppercase text-slate-500">Nombre</p><p className="text-white">{selected.nombre}</p></div>
                <div><p className="text-[10px] uppercase text-slate-500">Edad</p><p className="text-white">{selected.edad}</p></div>
                <div><p className="text-[10px] uppercase text-slate-500">Email</p><p className="text-emerald-400 font-mono text-xs">{selected.email}</p></div>
                <div><p className="text-[10px] uppercase text-slate-500">Sustancia</p><p className="text-white">{selected.sustancia}</p></div>
                <div><p className="text-[10px] uppercase text-slate-500">Años de consumo</p><p className="text-white">{selected.anos_consumo}</p></div>
                <div><p className="text-[10px] uppercase text-slate-500">Drip activo</p><p className="text-white">{selected.drip_active ? `Día ${selected.drip_last_day_sent}/21` : 'No'}</p></div>
              </div>
              {selected.salud_mental?.length > 0 && (
                <div><p className="text-[10px] uppercase text-slate-500 mb-1">Salud mental</p><p className="text-slate-300">{selected.salud_mental.join(', ')}</p></div>
              )}
              {selected.gatillos?.length > 0 && (
                <div><p className="text-[10px] uppercase text-slate-500 mb-1">Gatillos</p><p className="text-slate-300">{selected.gatillos.join(', ')}</p></div>
              )}
              {selected.fortalezas?.length > 0 && (
                <div><p className="text-[10px] uppercase text-slate-500 mb-1">Fortalezas</p><p className="text-slate-300">{selected.fortalezas.join(', ')}</p></div>
              )}
              {selected.para_que && (
                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4">
                  <p className="text-[10px] uppercase text-emerald-300 mb-1">Su "para qué"</p>
                  <p className="text-white italic">«{selected.para_que}»</p>
                </div>
              )}
              {selected.areas?.length > 0 && (
                <div><p className="text-[10px] uppercase text-slate-500 mb-1">Áreas</p><p className="text-slate-300">{selected.areas.join(', ')}</p></div>
              )}
              <div className="pt-3 border-t border-slate-800 text-[11px] text-slate-500">Lead ID: <code className="bg-slate-950 px-2 py-0.5 rounded">{selected.lead_id}</code></div>
              <div className="flex gap-2 pt-2">
                <a href={`mailto:${selected.email}`} className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black rounded-lg font-bold text-xs"><Mail className="w-3.5 h-3.5 inline mr-1.5" />Responder por email</a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const TREATMENT_LABELS = {
  senda: 'GRATUITO SENDA',
  online: 'Online',
  ambulatorio: 'Ambulatorio',
  residencial_estandar: 'Residencial Estándar',
  residencial_premium: 'Residencial Premium',
};

function CalculatorPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch('/api/admin/dashboard/calculator-stats');
      if (r.ok) setData(await r.json());
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };
  const isNew = (iso) => iso && Date.now() - new Date(iso).getTime() < 24 * 3600 * 1000;

  if (loading) return <div className="flex items-center gap-2 text-slate-400 py-12 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>;
  if (!data) return <div className="text-slate-400 py-8 text-center">Sin datos</div>;

  const total = data.total || 0;
  return (
    <div className="space-y-5">
      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatBox label="Total leads" value={data.total} color="slate" sub={<><Users className="w-3 h-3 inline mr-0.5" />all-time</>} />
        <StatBox label="Últimas 24h" value={data.last_24h} color="emerald" sub={<><Clock className="w-3 h-3 inline mr-0.5" />tiempo real</>} />
        <StatBox label="Últimos 7d" value={data.last_7d} color="emerald" />
        <StatBox label="Últimos 30d" value={data.last_30d} color="cyan" />
        <StatBox label="Patología dual" value={data.dual_pathology} color="amber" sub={`${data.dual_pathology_rate || 0}% del total`} />
        <StatBox label="" value="" color="slate" sub="" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Distribución por tipo */}
        {data.by_type?.length > 0 && (
          <Card className="bg-slate-950 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-300 flex items-center gap-2"><Target className="w-4 h-4" />Por tipo de tratamiento</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data.by_type.map(t => {
                  const pct = total ? Math.round(100 * t.count / total) : 0;
                  return (
                    <div key={t.type} className="flex items-center gap-3 text-sm">
                      <span className="w-44 text-slate-300 truncate">{TREATMENT_LABELS[t.type] || t.type}</span>
                      <div className="flex-1 h-5 bg-slate-900 rounded overflow-hidden">
                        <div className="h-full bg-emerald-500/70 flex items-center px-2 text-[10px] text-white font-bold" style={{ width: `${Math.max(pct, 4)}%` }}>{pct}%</div>
                      </div>
                      <span className="text-slate-400 text-xs w-10 text-right">{t.count}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Distribución por región */}
        {data.by_region?.length > 0 && (
          <Card className="bg-slate-950 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-300 flex items-center gap-2"><Target className="w-4 h-4" />Top regiones</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data.by_region.map(r => {
                  const pct = total ? Math.round(100 * r.count / total) : 0;
                  return (
                    <div key={r.region} className="flex items-center gap-3 text-sm">
                      <span className="w-44 text-slate-300 truncate capitalize">{r.region}</span>
                      <div className="flex-1 h-5 bg-slate-900 rounded overflow-hidden">
                        <div className="h-full bg-cyan-500/70 flex items-center px-2 text-[10px] text-white font-bold" style={{ width: `${Math.max(pct, 4)}%` }}>{pct}%</div>
                      </div>
                      <span className="text-slate-400 text-xs w-10 text-right">{r.count}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tabla leads */}
      <Card className="bg-slate-950 border-slate-800">
        <CardHeader className="pb-2 flex-row justify-between items-center">
          <CardTitle className="text-sm text-slate-300 flex items-center gap-2"><Mail className="w-4 h-4" />Últimos {data.leads?.length || 0} leads</CardTitle>
          <Button onClick={load} size="sm" variant="ghost" className="text-slate-400 hover:text-white"><RefreshCw className="w-3.5 h-3.5 mr-1" />Refrescar</Button>
        </CardHeader>
        <CardContent className="p-0">
          {!data.leads?.length ? (
            <div className="text-center py-12 text-slate-500 text-sm">Aún no hay leads.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                    <th className="px-4 py-3 text-left">Email</th>
                    <th className="px-4 py-3 text-left">Tipo</th>
                    <th className="px-4 py-3 text-left">Región</th>
                    <th className="px-4 py-3 text-left">Duración</th>
                    <th className="px-4 py-3 text-left">PD</th>
                    <th className="px-4 py-3 text-left">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {data.leads.map(l => (
                    <tr key={l.lead_id} className="border-b border-slate-800/60 hover:bg-slate-900/40">
                      <td className="px-4 py-3 text-slate-200 font-mono text-xs">
                        <div className="flex items-center gap-2">
                          {l.email}
                          {isNew(l.created_at) && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500 text-black font-bold">NUEVO</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-300">{TREATMENT_LABELS[l.treatment_type] || l.treatment_type}</td>
                      <td className="px-4 py-3 text-slate-400 capitalize">{l.region || '—'}</td>
                      <td className="px-4 py-3 text-slate-400">{l.duration ? `${l.duration} m` : '—'}</td>
                      <td className="px-4 py-3">{l.has_dual_pathology ? <span className="text-amber-400 text-xs">Sí</span> : <span className="text-slate-500 text-xs">No</span>}</td>
                      <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{fmtDate(l.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function EngagementDashboard() {
  const [tab, setTab] = useState('experiences');
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 border-b border-slate-800 pb-3 overflow-x-auto">
        <button
          onClick={() => setTab('experiences')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-[13px] whitespace-nowrap ${tab === 'experiences' ? 'border-emerald-500 text-emerald-400' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
          data-testid="engage-tab-experiences"
        >
          <TrendingUp className="w-4 h-4 inline mr-1.5" />Experiencias inmersivas
        </button>
        <button
          onClick={() => setTab('reto21')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-[13px] whitespace-nowrap ${tab === 'reto21' ? 'border-emerald-500 text-emerald-400' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
          data-testid="engage-tab-reto21"
        >
          <FileText className="w-4 h-4 inline mr-1.5" />Reto 21 Días
        </button>
        <button
          onClick={() => setTab('calculator')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-[13px] whitespace-nowrap ${tab === 'calculator' ? 'border-emerald-500 text-emerald-400' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
          data-testid="engage-tab-calculator"
        >
          <Calculator className="w-4 h-4 inline mr-1.5" />Calculadora
        </button>
      </div>
      {tab === 'experiences' && <ExperiencesPanel />}
      {tab === 'reto21' && <Reto21Panel />}
      {tab === 'calculator' && <CalculatorPanel />}
    </div>
  );
}
